import React, { useContext, useEffect, useState } from "react";
import {
  Drawer,
  IconButton,
  Typography,
  Slider,
  Avatar,
  Box,
  Button,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@mui/material";
import {
  Edit,
  Logout,
  VolumeUp,
  VolumeDown,
  VolumeOff,
  Dashboard,
  HelpOutline,
  Close,
  Wallet,
  ArrowDownward,
  AttachMoney,
} from "@mui/icons-material";
import StripeEarningsModal from "./StripeEarningsModal";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { getBadgeByLevel } from "../@shared/utils/Utils";
import { MainContext } from "../App";
import { getDatabase, ref, remove } from "firebase/database";
import { useSoundsStore } from "../@shared/store/useSoundsStore";
import AudioManager from "../@shared/utils/AudioManager";
import { colors, eyebrowTextSx, fonts, gradients } from "../@shared/ui/tokens";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../@shared/firebase/config";
import NeoButton from "./NeoButton";
import {
  PROFILE_IMAGES,
  BACKGROUND_IMAGES,
} from "../@shared/utils/profileImages";
import { useCardStore, useEnsureCardCatalog } from "../@shared/store/useCardsStore";
import EditProfileModal from "./EditProfileModal";
import { useChatsStore } from "../@shared/store/useChatsStore";
import { Settings } from "@mui/icons-material";
import AccountSettingsModal from "./AccountSettingsModal";
import useLatestAppRelease from "../@shared/hooks/useLatestAppRelease";
import { APP_VERSION, isVersionNewer } from "../@shared/utils/appVersion";


export default function SettingsDrawer({
  open,
  onClose,
  screen,
  onclick,
  //   sfxVolume,
  //   musicVolume,
  //   setSfxVolume,
  //   setMusicVolume,
  amtOfPlayers
}) {
  const { userData, logout } = useAuthStore();
  const { characterCards } = useCardStore();
  useEnsureCardCatalog({ enabled: open, infinity: false, character: true });
  const {
    setDrawerOpen,
    setActiveTab,
    unsubscribeLobby,
    unsubscribeParty,
  } = useChatsStore();
  const { navigate, setPartyId, setIsHost, Theme, accountSettingsVisible, setAccountSettingsVisible } = useContext(MainContext);
  const [editProfileVisible, setEditProfileVisible] = useState(false);
  const [stripeModalVisible, setStripeModalVisible] = useState(false);
  const { latestRelease } = useLatestAppRelease({ enabled: open });
  const latestVersion = latestRelease?.version;
  const hasNewerVersion =
    latestVersion && isVersionNewer(latestVersion, APP_VERSION);


  const sfxVolume = useSoundsStore((s) => s.sfxVolume);
  const setSfxVolume = useSoundsStore((s) => s.setSfxVolume);
  const musicVolume = useSoundsStore((s) => s.musicVolume);
  const setMusicVolume = useSoundsStore((s) => s.setMusicVolume);
  const isMusicMuted = useSoundsStore((s) => s.isMusicMuted);
  const toggleMusicMute = useSoundsStore((s) => s.toggleMusicMute);

  const [audioState, setAudioState] = useState(AudioManager.audioCtx.state);

  useEffect(() => {
    const checkState = () => setAudioState(AudioManager.audioCtx.state);
    const interval = setInterval(checkState, 500); // poll audio state
    return () => clearInterval(interval);
  }, []);

  const handleToggleMute = async () => {
    if (audioState !== "running") {
      try {
        console.log("🔊 Resuming audio context...");
        await AudioManager.audioCtx.resume();
        setAudioState("running");
      } catch (err) {
        console.error("Audio resume failed:", err);
      }
      return;
    }

    toggleMusicMute();
  };

  const onVolumeChange = async (evt, value) => {
    setMusicVolume(value);
    AudioManager.setMusicVolume(value);

    if (AudioManager.audioCtx.state !== "running") {
      try {
        console.log("🔊 Resuming audio context from volume slider...");
        await AudioManager.audioCtx.resume();
      } catch (err) {
        console.error("AudioContext resume failed:", err);
      }
    }
  };

  // const onVolumeChange = (evt, value) => {
  //   setMusicVolume(value);
  //   AudioManager.setMusicVolume(value);
  // };

  useEffect(() => {
    setTimeout(() => {
      setPartyId(null);
      setIsHost(null);
    }, 2000);
  }, []);

  useEffect(() => {
    // whenever the zustand store’s musicVolume changes,
    // tell the AudioManager to update the actual <audio> node
    AudioManager.setMusicVolume(musicVolume);
  }, [musicVolume]);

  const handleLogout = () => {
    logout();
    alert("Logged Out: You have been signed out.");
  };

  const removePlayerPresence = async () => {

    unsubscribeLobby();
    // If the chat drawer is on "lobby", switch it so the UI doesn’t point to a dead feed
    setActiveTab("party");
    setDrawerOpen(false);
    const rtdb = getDatabase();

    const pId = userData.partyId

    const userDocRef = doc(db, "users", userData.id);
    const userExitPatch = {
      partyId: null,
      isHost: null,
    };

    if (userData?.activeGameMode === "story") {
      userExitPatch.storyModeSolo = false;
    }

    await updateDoc(userDocRef, {
      ...userExitPatch,
    });
    const presenceRef = ref(rtdb, `presence/${userData.id}`);
    await remove(presenceRef);

    // Last player out cleans up the party chat. The RTDB rules may not grant
    // delete on partyChats, so a denial must not abort leaving the world.
    if (amtOfPlayers === 1 && pId) {
      try {
        await remove(ref(rtdb, `partyChats/${pId}`));
      } catch (error) {
        console.warn("[leaveWorld] party chat cleanup skipped:", error?.message);
      }
    }
  };

  const Stud = ({ sx }) => (
    <Box
      sx={{
        position: "absolute",
        width: 10,
        height: 10,
        borderRadius: "50%",
        background:
          "radial-gradient(ellipse at 30% 30%, #ffd24d 0%, #ffb400 35%, #7a4e00 100%)",
        boxShadow: "0 0 6px rgba(255,200,50,.6)",
        ...sx,
      }}
    />
  );

  // if (!userData) return null;

  return (
    <>
      {userData && (
        <Drawer
          anchor="left"
          open={open}
          onClose={onClose}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
          // PaperProps={{ sx: { width: 300, bgcolor: "#16116e", p: 2 } }}
          PaperProps={{
            sx: {
              width: { xs: 340, sm: 380 },
              p: 0,
              overflow: "visible",
              borderRadius: 0,
              background: `${gradients.panelSheen}, linear-gradient(180deg, #1a0e3d 0%, #0e0724 100%)`,
              borderRight: `1px solid ${colors.lineStrong}`,
              boxShadow: "24px 0 60px rgba(0,0,0,0.55)",
              color: "#fff",
              position: "relative",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {/* TOP CAP / HEADER */}
          <Box
            sx={{
              position: "relative",
              px: 3,
              pt: 2.5,
              pb: 2,
              borderBottom: `1px solid ${colors.line}`,
              "&::after": {
                content: '""',
                position: "absolute",
                left: 24,
                right: 24,
                bottom: -1,
                height: "1px",
                background: gradients.goldLine,
              },
            }}
          >
            <Typography sx={{ ...eyebrowTextSx, color: colors.gold }}>
              InfiNFT Royale
            </Typography>
            <Typography
              sx={{
                fontFamily: fonts.display,
                fontSize: 30,
                lineHeight: 1.1,
                color: "#fff",
                letterSpacing: "0.02em",
              }}
            >
              Menu
            </Typography>

            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 18,
                right: 16,
                color: "#fff",
                border: `1px solid ${colors.lineStrong}`,
                bgcolor: "rgba(255,255,255,0.06)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
              }}
            >
              <Close />
            </IconButton>

          </Box>

          {/* CONTENT */}
          <Box sx={{ p: 2, pb: 3, overflowY: "auto", flex: 1 }}>
            <Box
              sx={{
                position: "relative",
                p: 2,
                borderRadius: "16px",
                background:
                  "linear-gradient(135deg, rgba(124,92,255,0.28) 0%, rgba(34,18,80,0.6) 60%, rgba(14,7,36,0.8) 100%)",
                border: `1px solid ${colors.lineStrong}`,
                boxShadow: "0 12px 28px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,0.1)",
                mb: 2,
                display: "flex",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  width: { xs: 54, md: 64 },
                  height: { xs: 54, md: 64 },
                  border: `2px solid ${colors.gold}`,
                  boxShadow: "0 0 14px rgba(255,211,106,0.35)",
                  borderRadius: "12px",
                  containerType: "inline-size",
                  // overflow:'hidden'
                }}
              >
                <img
                  src={userData?.imageBg || BACKGROUND_IMAGES[0].img}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: "100%",
                    objectFit: "cover",
                    zIndex: 0,
                    borderRadius: "6px",
                  }}
                  alt=""
                />
                <img
                  src={userData?.imageUrl || PROFILE_IMAGES[0].img}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    zIndex: 1,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    position: "absolute",
                    bottom: "-15%",
                    right: "-20%",
                    zIndex: 2,
                    width: 36,
                    height: 36,
                  }}
                >
                  <Box
                    sx={{ positon: "relative", width: "100%", height: "100%" }}
                  >
                    <img
                      src={getBadgeByLevel(userData?.level)?.badge}
                      alt="badge"
                      style={{
                        width: "100%",
                        height: "auto",
                        filter: "drop-shadow(0 0 6px #0a5cff66)",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 2,
                      }}
                    />
                    <Typography
                      sx={{
                        color: "#ffffffff",
                        fontWeight: 700,
                        fontSize: "20cqw",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 3,
                        textShadow:
                          "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
                      }}
                    >
                      {userData.level}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  containerType: "inline-size",
                  paddingLeft: "5%",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: fonts.display,
                    color: "#fff",
                    fontSize: "7cqw",
                    lineHeight: 1.15,
                  }}
                >
                  @{userData?.username}
                </Typography>
                <Typography sx={{ color: "#c9b7ff", fontSize: "5cqw" }}>
                  {getBadgeByLevel(userData?.level)?.name}
                </Typography>
                <Box
                  sx={{
                    marginTop: "3%",
                    width: "90%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "rgba(7,3,15,0.45)",
                    border: `1px solid ${colors.line}`,
                    padding: "3% 5%",
                    borderRadius: "10px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "fit-content",
                    }}
                  >
                    <Box
                      component={"img"}
                      sx={{ width: "20%", height: "auto", mr: "2%" }}
                      src={require("../@shared/assets/infift_token.png")}
                    />
                    <Typography sx={{ fontSize: "4.5cqw", color: "#fff" }}>
                      {userData?.infinityTokens ?? 0}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "fit-content",
                    }}
                  >
                    <Box
                      component={"img"}
                      sx={{
                        width: "15%",
                        height: "auto",
                        mr: "2%",
                        rotate: "-15deg",
                      }}
                      src={require("../@shared/assets/back-of-card.png")}
                    />
                    <Typography sx={{ fontSize: "4.5cqw", color: "#fff" }}>
                      {userData?.nftCards?.length ?? 0}/{characterCards?.length ?? 0}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      width: "fit-content",
                    }}
                  >
                    <Box
                      component={"img"}
                      sx={{ width: "25%", height: "auto", mr: "2%" }}
                      src={require("../@shared/assets/icons/trophy_icon.png")}
                    />
                    <Typography sx={{ fontSize: "4.5cqw", color: "#fff" }}>
                      {userData?.record?.wins ?? 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  right: -5,
                  top: -12,
                  zIndex: 2,
                  width: "50px",
                }}
              >
                <NeoButton
                  // text="Edit"
                  isIcon={true}
                  width={40}
                  color="#3478F6"
                  // width={50}
                  borderRadius={12}
                  icon={<Edit sx={{ color: "#fff" }} />}
                  onClick={() => {
                    setEditProfileVisible(true);
                    onClose();
                  }}
                />
              </Box>
            </Box>

            {(userData.roles.includes("test") ||
              userData.roles.includes("ip_provider") ||
              userData.roles.includes("collection_creator")) && (
                <Accordion
                  sx={sectionAccordionSx}
                >
                  <AccordionSummary expandIcon={<ArrowDownward style={{ color: "white" }} />}>
                    <Typography sx={sectionTitleSx}>
                      Earnings
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <NeoButton
                      text={
                        userData.stripeId || userData.stripeTestId
                          ? "DASHBOARD"
                          : "CONNECT PAYMENTS"
                      }
                      // variant="outlined"
                      // fullWidth
                      color={
                        userData.stripeId || userData.stripeTestId
                          ? "#3478F6"
                          : "#9b59b6"
                      }
                      width={100}
                      height="44px"
                      startAdorn={
                        userData.stripeId || userData.stripeTestId ? (
                          <AttachMoney sx={{ mr: 1 }} />
                        ) : (
                          <Wallet sx={{ mr: 1 }} />
                        )
                      }
                      onClick={() => {
                        userData.stripeId && userData.onboardingComplete
                          ? navigate("/dashboard")
                          : setStripeModalVisible(true);
                      }}
                    // startIcon={
                    //   userData.stripeId || userData.stripeTestId ? (
                    //     <AttachMoney />
                    //   ) : (
                    //     <Wallet />
                    //   )
                    // }
                    // sx={{ color: "white", borderColor: "white", mt: 1 }}
                    >
                      {/* {userData.stripeId || userData.stripeTestId ? (
                        <>
                          Dasboard <Dashboard sx={{ ml: 1 }} />
                        </>
                      ) : (
                        <Typography sx={{ color: "yellow" }}>
                          Not Connected (click to connect)
                        </Typography>
                      )} */}
                    </NeoButton>
                  </AccordionDetails>
                </Accordion>
              )}

            {/* AUDIO */}
            <Accordion
              sx={sectionAccordionSx}
            >
              <AccordionSummary
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
                expandIcon={
                  isMusicMuted ? (
                    <VolumeOff
                      onClick={handleToggleMute}
                      sx={{
                        color: "#e2d5ff",
                        borderColor: "#4a3ab7",
                        "&:hover": { borderColor: "#6a58ff" },
                      }}
                    />
                  ) : (
                    <VolumeUp
                      onClick={handleToggleMute}
                      sx={{
                        color: "#e2d5ff",
                        borderColor: "#4a3ab7",
                        "&:hover": { borderColor: "#6a58ff" },
                      }}
                    />
                  )
                }
              >
                <Typography sx={sectionTitleSx}>
                  Audio
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: "#e2d5ff", fontWeight: 800 }}>
                  SFX Volume
                </Typography>
                <Slider
                  value={sfxVolume}
                  onChange={(e, newVal) => setSfxVolume(newVal)}
                  min={0}
                  max={1}
                  step={0.01}
                  sx={sliderSX}
                />

                <Typography sx={{ color: "#e2d5ff", fontWeight: 800, mt: 1 }}>
                  Music Volume
                </Typography>
                <Slider
                  value={musicVolume}
                  onChange={onVolumeChange}
                  min={0}
                  max={1}
                  step={0.01}
                  sx={sliderSX}
                />
                <Box mt={1}></Box>
              </AccordionDetails>
            </Accordion>

            {/* SUPPORT */}

            <Accordion
              sx={sectionAccordionSx}
            >
              <AccordionSummary
                expandIcon={<ArrowDownward style={{ color: "white" }} />}>
                <Typography sx={sectionTitleSx}>
                  Support
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <NeoButton
                  // startIcon={<HelpOutline />}
                  // variant="contained"
                  // color="primary"
                  text="SUPPORT"
                  color="#3478F6"
                  width={100}
                  height="44px"
                  startAdorn={<HelpOutline sx={{ mr: 1 }} />}
                  onClick={() => console.log("Support tapped")}
                // fullWidth
                />

                <Box sx={{ width: "100%", py: 1 }} />

                <NeoButton
                  // startIcon={<HelpOutline />}
                  // variant="contained"
                  // color="primary"
                  text="FAQs"
                  color="#3478F6"
                  width={100}
                  height="44px"
                  // startAdorn={<HelpOutline sx={{ mr: 1 }} />}
                  onClick={() => console.log("Support tapped")}
                // fullWidth
                />
              </AccordionDetails>
            </Accordion>

            {/* ACCOUNT SETTINGS */}
            <Accordion
              expanded={false} // never expands
              onChange={(event) => {
                // clicking anywhere opens the modal
                setAccountSettingsVisible(true);
                if (onClose) onClose();
              }}
              sx={sectionAccordionSx}
            >
              <AccordionSummary>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Settings sx={{ color: colors.gold }} />
                  <Typography sx={{ ...sectionTitleSx, mb: 0 }}>
                    Account Settings
                  </Typography>
                </Box>
              </AccordionSummary>
              {/* Intentionally no AccordionDetails — this behaves more like a trigger row */}
            </Accordion>


            {/* LEAVE / LOG OUT */}
            <NeoButton
              // startIcon={<Logout />}
              // variant="contained"
              // color="error"
              text={
                screen === "world" || screen === "lobby"
                  ? "LEAVE"
                  : screen === "gameboard"
                    ? "QUIT MATCH"
                    : "LOG OUT"
              }
              color="#e54848"
              width={100}
              height="48px"
              startAdorn={<Logout sx={{ mr: 1 }} />}
              onClick={() => {
                screen === "world"
                  ? removePlayerPresence()
                  : screen === "gameboard" || screen === "lobby"
                    ? onclick()
                    : handleLogout();
              }}
            // fullWidth
            >
              {/* {screen === "world" || screen === "lobby"
                  ? "Leave"
                  : screen === "gameboard"
                    ? "Quit Match"
                    : "Log Out"} */}
            </NeoButton>
            <Typography
              sx={{
                textAlign: "center",
                color: "#9aa0ff",
                fontSize: 12,
                mt: 1.5,
              }}
            >
              InfiNFT Royale v{APP_VERSION}
              {hasNewerVersion ? ` | Latest v${latestVersion}` : ""}
            </Typography>
          </Box>
        </Drawer>
      )}

      <EditProfileModal
        visible={editProfileVisible}
        onClose={() => setEditProfileVisible(false)}
        userData={userData}
      />

      <StripeEarningsModal
        visible={stripeModalVisible}
        onClose={() => setStripeModalVisible(false)}
      />

      <AccountSettingsModal
        visible={accountSettingsVisible}
        onClose={() => setAccountSettingsVisible(false)}
        userData={userData}
      />

    </>
  );
}

/* ---------- styles ---------- */

const sectionAccordionSx = {
  borderRadius: "14px !important",
  background: "rgba(255,255,255,0.04)",
  border: `1px solid ${colors.line}`,
  boxShadow: "none",
  color: "#fff",
  mb: 1.25,
  mt: 0,
  "&::before": { display: "none" },
  "&:hover": { background: "rgba(255,255,255,0.07)" },
  "& .MuiAccordionSummary-root": { minHeight: 52 },
  "& .MuiAccordionSummary-content": { my: 1.25 },
  "& .MuiAccordionSummary-expandIconWrapper": { color: colors.textSoft },
};

const sectionTitleSx = {
  color: "#fff",
  fontWeight: 800,
  fontSize: "0.82rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  mb: 0,
};

const sliderSX = {
  color: "#ffd36a",
  height: 6,
  "& .MuiSlider-rail": {
    opacity: 1,
    backgroundColor: "#2b1d63",
  },
  "& .MuiSlider-track": {
    border: "none",
    background: "linear-gradient(90deg, #e9a334 0%, #ffd36a 100%)",
  },
  "& .MuiSlider-thumb": {
    width: 16,
    height: 16,
    backgroundColor: "#ffe9a8",
    border: "3px solid #3a1fa4",
    boxShadow: "0 0 8px #ff7ce588",
  },
};
